import NameLinkCell from '@/components/NameLinkCell';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { type FC, memo } from 'react';

import styles from './SampleNameCell.module.css';

// The study samples tables disagree on the sample's field names: the proteoform-grain tables
// carry sample_id/sample_name, StudySamplesTable's rows are samples themselves (id/name).
export type SampleNameRow = { sample_id: string; sample_name: string } | { id: string; name: string };

export interface SampleNameCellProps {
  data?: SampleNameRow;
  /** Hoisted per table — see useCanOpenSampleDetail. */
  canOpenSample: boolean;
}

function resolveSample(data: SampleNameRow): { id?: string; name?: string } {
  return 'sample_id' in data ? { id: data.sample_id, name: data.sample_name } : { id: data.id, name: data.name };
}

// The testid keys on sample id, which repeats down a proteoform-grain column — it identifies the
// sample, not the row. Match it with a prefix selector rather than a strict getByTestId.
const SampleNameCellComponent: FC<SampleNameCellProps> = ({ data, canOpenSample }) => {
  if (!data) return null;
  const { id, name } = resolveSample(data);

  // /samples/$sampleId is super_user-only, but the study pages hosting these tables are not
  // role-gated at all. Linking unconditionally would hand every other role a click that only
  // lands on /unauthorized, so they keep the plain text they saw before this column was linked.
  if (!id || !canOpenSample) {
    return name !== undefined && name !== '' ? <Text as="span">{name}</Text> : null;
  }

  return (
    <NameLinkCell
      className={styles.sampleName}
      fullHeight
      id={id}
      name={name !== undefined && name !== '' ? name : id}
      params={{ sampleId: id }}
      testId={`sample-name-link-${id}`}
      to="/samples/$sampleId"
    />
  );
};

export default memo(SampleNameCellComponent);
